import React, { useState } from 'react';
import { FileText, Copy, AlertTriangle, CheckCircle } from 'lucide-react';

const PageTitleAnalyzerTool = () => {
  const [title, setTitle] = useState('');
  const [keyword, setKeyword] = useState('');
  const [analysis, setAnalysis] = useState(null);
  const [copied, setCopied] = useState(false);

  const analyzeTitle = () => {
    if (!title.trim()) {
      setAnalysis(null);
      return;
    }

    const titleText = title.trim();
    const length = titleText.length;
    const pixelWidth = estimatePixelWidth(titleText);
    
    const issues = [];
    const suggestions = [];
    let score = 100;

    // Length check (50-60 characters optimal)
    if (length < 30) {
      issues.push({ type: 'warning', text: 'Title is too short. Aim for 50-60 characters.' });
      score -= 15;
    } else if (length > 60) {
      issues.push({ type: 'warning', text: `Title may be truncated in search results (${length} characters)` });
      score -= 10;
    } else if (length >= 50 && length <= 60) {
      issues.push({ type: 'success', text: 'Title length is optimal (50-60 characters)' });
    }

    // Pixel width check (600px max for Google)
    if (pixelWidth > 600) {
      issues.push({ type: 'warning', text: `Title may be truncated (estimated ${pixelWidth}px, max 600px)` });
      score -= 10;
    }

    // Keyword check
    if (keyword.trim()) {
      const kw = keyword.trim().toLowerCase();
      const titleLower = titleText.toLowerCase();
      
      if (titleLower.includes(kw)) {
        const position = titleLower.indexOf(kw);
        if (position === 0) {
          issues.push({ type: 'success', text: 'Keyword is at the beginning - excellent for SEO' });
        } else if (position < 30) {
          issues.push({ type: 'success', text: 'Keyword is in the first half - good placement' });
        } else {
          issues.push({ type: 'info', text: 'Keyword found but consider moving it earlier' });
          score -= 5;
        }
      } else {
        issues.push({ type: 'warning', text: 'Target keyword not found in title' });
        score -= 20;
        suggestions.push('Include your target keyword in the title');
      }
    }

    // Check for common issues
    if (titleText.toUpperCase() === titleText && titleText.length > 10) {
      issues.push({ type: 'warning', text: 'All caps titles look spammy - use title case instead' });
      score -= 10;
    }

    if (/(.)\1{3,}/.test(titleText)) {
      issues.push({ type: 'warning', text: 'Avoid excessive repeated characters' });
      score -= 5;
    }

    if (titleText.includes('|') || titleText.includes('-') || titleText.includes(':')) {
      issues.push({ type: 'info', text: 'Separator detected - make sure brand/site name is at the end' });
    }

    // Word count
    const wordCount = titleText.split(/\s+/).length;
    if (wordCount < 4) {
      suggestions.push('Consider adding more descriptive words');
    }

    // Power words
    const powerWords = ['free', 'new', 'best', 'guide', 'how', 'top', 'ultimate', 'complete', 'easy', '2025', '2026'];
    const hasPowerWord = powerWords.some(word => titleText.toLowerCase().includes(word));
    if (hasPowerWord) {
      issues.push({ type: 'success', text: 'Contains attention-grabbing power words' });
    } else {
      suggestions.push('Consider adding power words like "Best", "Guide", "Free", or current year');
    }

    // Numbers
    if (/\d/.test(titleText)) {
      issues.push({ type: 'success', text: 'Contains numbers - attracts more clicks' });
    }

    setAnalysis({
      length,
      pixelWidth,
      wordCount,
      issues,
      suggestions,
      score: Math.max(0, score)
    });
  };

  const estimatePixelWidth = (text) => {
    // Rough estimation based on average character width
    return Math.round(text.length * 8.5);
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-400';
    if (score >= 60) return 'text-yellow-400';
    return 'text-red-400';
  };

  const copyTitle = () => {
    navigator.clipboard.writeText(title);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">Page Title</label>
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Enter your page title..."
          className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:border-blue-500"
        />
        <div className="flex justify-between text-xs text-gray-500 mt-1">
          <span>{title.length} characters</span>
          <span>Recommended: 50-60 characters</span>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-300 mb-2">Target Keyword (optional)</label>
        <input
          type="text"
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
          placeholder="e.g., SEO tools"
          className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:border-blue-500"
        />
      </div>

      {/* Google Preview */}
      <div className="p-4 bg-white rounded-lg">
        <div className="text-sm text-gray-500 mb-2">Google Search Preview</div>
        <div className="text-blue-600 text-lg hover:underline cursor-pointer truncate" style={{ maxWidth: '600px' }}>
          {title || 'Your Page Title Will Appear Here'}
        </div>
        <div className="text-green-700 text-sm">https://example.com/page</div>
        <div className="text-gray-600 text-sm mt-1">
          Meta description preview text appears here. This is what users will see in search results...
        </div>
      </div>

      <button
        onClick={analyzeTitle}
        disabled={!title.trim()}
        className="flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-700 disabled:cursor-not-allowed text-white rounded-lg transition-colors font-medium"
      >
        <FileText size={18} />
        Analyze Title
      </button>

      {analysis && (
        <div className="space-y-4">
          {/* Score */}
          <div className="p-4 bg-gray-800 rounded-lg flex items-center justify-between">
            <span className="text-gray-400">SEO Title Score</span>
            <span className={`text-3xl font-bold ${getScoreColor(analysis.score)}`}>
              {analysis.score}/100
            </span>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-4">
            <div className="p-3 bg-gray-800 rounded-lg text-center">
              <div className={`text-xl font-bold ${analysis.length > 60 ? 'text-yellow-400' : 'text-green-400'}`}>
                {analysis.length}
              </div>
              <div className="text-xs text-gray-500">Characters</div>
            </div>
            <div className="p-3 bg-gray-800 rounded-lg text-center">
              <div className={`text-xl font-bold ${analysis.pixelWidth > 600 ? 'text-yellow-400' : 'text-green-400'}`}>
                ~{analysis.pixelWidth}px
              </div>
              <div className="text-xs text-gray-500">Pixel Width</div>
            </div>
            <div className="p-3 bg-gray-800 rounded-lg text-center">
              <div className="text-xl font-bold text-blue-400">{analysis.wordCount}</div>
              <div className="text-xs text-gray-500">Words</div>
            </div>
          </div>

          {/* Issues */}
          <div className="space-y-2">
            {analysis.issues.map((issue, i) => (
              <div
                key={i}
                className={`flex items-start gap-2 p-3 rounded-lg ${
                  issue.type === 'success' ? 'bg-green-900/30 text-green-400' :
                  issue.type === 'warning' ? 'bg-yellow-900/30 text-yellow-400' :
                  'bg-blue-900/30 text-blue-400'
                }`}
              >
                {issue.type === 'success' ? <CheckCircle size={16} /> : 
                 issue.type === 'warning' ? <AlertTriangle size={16} /> : 
                 <CheckCircle size={16} />}
                <span className="text-sm">{issue.text}</span>
              </div>
            ))}
          </div>

          {/* Suggestions */}
          {analysis.suggestions.length > 0 && (
            <div className="p-4 bg-blue-900/30 border border-blue-700 rounded-lg">
              <h3 className="text-sm font-medium text-blue-300 mb-2">Suggestions</h3>
              <ul className="space-y-1 text-sm text-gray-400">
                {analysis.suggestions.map((s, i) => (
                  <li key={i}>• {s}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      <div className="flex gap-3">
        <button
          onClick={copyTitle}
          disabled={!title}
          className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-700 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
        >
          <Copy size={16} />
          {copied ? 'Copied!' : 'Copy Title'}
        </button>
      </div>
    </div>
  );
};

export default PageTitleAnalyzerTool;
